import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

import { RequestLog } from '@engine/models'

import { Table } from '#components/Table'

import { TenderStatusBadge } from './TenderStatusBadge'

interface TendersListProps {
  tenders: RequestLog[]
  selectedId?: string
  onSelect: (tender: RequestLog) => void
  infiniteScrollRef?: React.RefObject<HTMLDivElement>
  isFetchingMore?: boolean
}

const columnHelper = createColumnHelper<RequestLog>()

const TendersList = ({
  tenders,
  selectedId,
  onSelect,
  infiniteScrollRef,
  isFetchingMore,
}: TendersListProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('externalId', {
        header: 'External ID',
        cell: info => (
          <div className="truncate max-w-[200px]">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('tenantResponse.content.pickup.confNum', {
        header: 'Pickup#',
        cell: info => <div className="text-xs">{info.getValue()}</div>,
      }),
      columnHelper.accessor('requestId', {
        header: 'Request ID',
        cell: info => <div className="text-xs">{info.getValue()}</div>,
      }),
      columnHelper.accessor('carrierCode', {
        header: 'Carriers',
        cell: info => {
          const carriers = info.getValue()
          return carriers && carriers.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {carriers.map((scac, idx) => (
                <span key={idx} className="badge badge-sm badge-info uppercase">
                  {scac}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs opacity-50">N/A</span>
          )
        },
      }),
      columnHelper.display({
        header: 'Status',
        cell: info => {
          return <TenderStatusBadge log={info.row.original} />
        },
      }),
      columnHelper.accessor('tenantRequest.requestDate', {
        header: 'Request Date',
        cell: info => {
          const date = info.getValue()
          return date ? (
            <span className="text-xs">{new Date(date).toLocaleString()}</span>
          ) : null
        },
      }),
      columnHelper.accessor('tenantResponse.responseTime', {
        header: 'Response Time',
        cell: info => {
          const time = info.getValue()
          if (!time) return <span className="text-xs opacity-50">N/A</span>
          const formatted =
            time < 1000 ? `${time}ms` : `${(time / 1000).toFixed(2)}s`
          const colorClass =
            time < 1000
              ? 'text-success'
              : time < 3000
                ? 'text-warning'
                : 'text-error'
          return (
            <span className={`text-xs font-semibold ${colorClass}`}>
              {formatted}
            </span>
          )
        },
      }),
    ],
    [],
  )

  return (
    <Table
      data={tenders}
      columns={columns}
      getRowId={tender => tender._id}
      selectedId={selectedId}
      onRowClick={onSelect}
      infiniteScrollRef={infiniteScrollRef}
      isFetchingMore={isFetchingMore}
    />
  )
}

export { TendersList }
